<script setup>
import { computed } from "vue";
import moment from "moment";
import { storeToRefs } from "pinia";
import { isEnabledFlag } from "../helpers";
import AlertError from "./AlertError.vue";
import SectionTitle from "./SectionTitle.vue";
import useStore from "../store";

const props = defineProps({
  booking: {
    type: Object,
    default: null,
  },
  item: {
    type: Object,
    default: null,
  },
  itemInd: {
    type: [String, Number],
    default: null,
  },
  mode: {
    type: String,
    default: "item",
  },
  saving: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["close", "confirm"]);
const store = useStore();
const { attractions } = storeToRefs(store);

const affectedItems = computed(() => {
  if (props.mode === "booking") {
    return props.booking?.items ?? [];
  }

  return props.item ? [props.item] : [];
});

const itemCountLabel = computed(() => {
  const count = affectedItems.value.length;
  return count === 1 ? "1 booking item" : `${count} booking items`;
});

const title = computed(() =>
  props.mode === "booking" ? "Cancel entire booking" : "Cancel booking item"
);

const intro = computed(() =>
  props.mode === "booking"
    ? "This will cancel every upcoming item in this booking."
    : "This will cancel the selected booking item."
);

const confirmLabel = computed(() =>
  props.mode === "booking" ? "Confirm cancellation" : "Cancel this item"
);

const attractionFromProductCode = (productCode) =>
  attractions.value.find(
    (current) => current?.rezdy_retail_product_code === productCode
  ) ?? null;

const cancelBookingsEnabled = (item) => {

  if (item?.productCode === 'PYY5D6'){
    return false;
  }

  return isEnabledFlag(attractionFromProductCode(item?.productCode)?.enabled_cancel_bookings);
};

const validationMessage = computed(() => {
  if (affectedItems.value.some((item) => !cancelBookingsEnabled(item))) {
    return props.mode === "booking"
      ? "One or more booking items can’t be cancelled online."
      : "This booking item can’t be cancelled online.";
  }

  if (props.mode === "booking") {
    return affectedItems.value.length ? "" : "There are no booking items available to cancel.";
  }

  return props.item ? "" : "This booking item can’t be cancelled right now.";
});

const itemFallsOutsideRefundWindow = (item) => {
  // week for testing
  // return moment(item?.startTimeLocal).isSame(moment(), 'week');

  const startTime = item?.startTimeLocal ?? item?.startTime ?? null;

  if (!startTime) {
    return false;
  }

  const refundCutoff = moment(startTime)
    .startOf("day")
    .subtract(1, "day")
    .hour(17)
    .minute(0)
    .second(0)
    .millisecond(0);

  return moment().isSameOrAfter(refundCutoff);
};

const noRefundWarning = computed(() => {
  if (!affectedItems.value.some((item) => itemFallsOutsideRefundWindow(item))) {
    return "";
  }

  return `This cancellation is still allowed, but it falls outside the refund window in the
        <a href="/terms-conditions/" target="_blank" class="text-primary underline hover:text-primary/80">terms and conditions </a>
        so no refund will be issued.`;
});

const formatTime = (item) => {
  const time = item?.startTimeLocal ?? item?.startTime ?? null;

  return time ? moment(time).format("dddd Do MMMM YYYY, h:mma") : "Time unavailable";
};

const close = () => {
  if (props.saving) {
    return;
  }

  emit("close");
};

const confirm = () => {
  if (validationMessage.value) {
    return;
  }

  emit("confirm", {
    mode: props.mode,
    item: props.item,
    itemInd: props.itemInd,
  });
};
</script>

<template>
  <div class="w-full px-4 pb-8 pt-2 lg:px-0 lg:pb-12">
    <div class="mx-auto w-full max-w-5xl rounded-[32px] bg-[#F4F1EB] p-6 text-dark lg:p-10">
      <div class="flex items-start justify-between gap-4 border-b border-primary/30 pb-6">
        <div>
          <div class="text-sm text-dark/60">
            Manage booking
          </div>
          <h2 class="mt-2 text-3xl font-bold lg:text-4xl">{{ title }}</h2>
          <p class="mt-3 max-w-2xl text-lg text-dark/70">
            {{ intro }} This action can’t be undone, so please confirm before continuing.
          </p>
          <p class="mt-3 max-w-2xl text-lg text-dark/70">
            Bookings cancelled after 5pm AWST the day before your tour will not be
            valid for a refund. Read our
            <a href="/terms-conditions/" target="_blank" class="text-primary underline hover:text-primary/80">Terms and Conditions</a>
            for more details.
          </p>
        </div>

        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full border-2 border-dark bg-white px-5 py-2.5 font-bold text-dark"
          :disabled="saving"
          @click="close"
        >
          Back to order
        </button>
      </div>

      <div class="grid gap-8 pt-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div class="rounded-[28px] bg-white px-6 py-6 lg:px-8">
          <SectionTitle :hide-number="true">
            About to cancel
          </SectionTitle>

          <div class="space-y-4">
            <div class="rounded-2xl border border-[#D8CBB8] bg-[#FFF8EE] px-5 py-4 text-dark">
              <div class="text-lg font-semibold">{{ itemCountLabel }}</div>
            </div>

            <ul class="space-y-3">
              <li
                v-for="currentItem in affectedItems"
                :key="currentItem.id"
                class="rounded-2xl border border-primary/20 bg-light px-5 py-4"
              >
                <div class="text-lg font-semibold">
                  {{ currentItem.productName }}
                </div>
                <div class="mt-1 text-dark/70">
                  {{ formatTime(currentItem) }}
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="rounded-[28px] bg-light px-6 py-6 lg:px-8">
          <SectionTitle :hide-number="true">
            Confirm booking cancellation
          </SectionTitle>

          <div class="space-y-5">
            <p class="text-base text-dark/80">
              Once cancelled, you'll receive a confirmation email with details about the cancellation.
            </p>

            <AlertError v-if="error">
              {{ error }}
            </AlertError>

            <AlertError v-else-if="validationMessage">
              {{ validationMessage }}
            </AlertError>

            <div
              v-else-if="noRefundWarning"
              class="rounded-2xl border-2 border-[#D8CBB8] bg-[#FFF8EE] px-5 py-4 text-xl text-dark"
              v-html="noRefundWarning">
            </div>
          </div>
        </div>
      </div>

      <div class="mt-8 flex flex-col-reverse gap-3 border-t border-primary/30 pt-6 sm:flex-row sm:justify-end">
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full border-2 border-dark bg-white px-6 py-3 font-bold text-dark"
          :disabled="saving"
          @click="close"
        >
          Keep booking
        </button>

        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full bg-dark px-6 py-3 font-bold text-light disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="saving || !!validationMessage"
          @click="confirm"
        >
          {{ saving ? "Cancelling..." : confirmLabel }}
        </button>
      </div>
    </div>
  </div>
</template>
